import React, { useState, useEffect, useRef } from "react";
import ReviewsStatsChart from "../components/ReviewsStatsChart";
import { useNavigate } from "react-router-dom";
import { AnalyticsResponse } from "../types";
import { API_CONFIG, getApiUrl } from "../config/api";

export const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isStoresLoading, setIsStoresLoading] = useState(true);
  const [storesStats, setStoresStats] = useState<
    Array<{
      id: string;
      name: string;
      platform: string;
      avgRating: number;
      count: number;
    }>
  >([]);
  const didInitRef = useRef(false);

  const navigate = useNavigate();

  const getPlatformBadgeClasses = (platform: string) => {
    if (platform === "ozon") return "bg-blue-100 text-blue-700 border-blue-200";
    if (platform === "wildberries")
      return "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200";
    return "bg-yellow-100 text-yellow-700 border-yellow-200"; // yandex
  };

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    fetchStats();
    fetchStoresStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ANALYTICS), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data: AnalyticsResponse = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Ошибка при загрузке статистики:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStoresStats = async () => {
    try {
      setIsStoresLoading(true);
      const token = localStorage.getItem("token");
      const storesResp = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.STORES), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!storesResp.ok) return;
      const stores: Array<{ id: string; name: string; platform: string }> =
        await storesResp.json();

      const perStoreStats = await Promise.all(
        stores.map(async (s) => {
          try {
            const headers = { Authorization: `Bearer ${token}` } as Record<
              string,
              string
            >;
            if (s.platform === "ozon") {
              const url = getApiUrl(API_CONFIG.ENDPOINTS.OZON_REVIEWS);
              const resp = await fetch(`${url}?limit=100&storeId=${s.id}`, {
                headers,
              });
              const data = await resp.json();
              const list: Array<{ rating?: number }> = data?.reviews || [];
              const { sum, count } = list.reduce(
                (acc, r) => {
                  const rating = Number(r?.rating || 0);
                  if (Number.isFinite(rating) && rating > 0) {
                    acc.sum += rating;
                    acc.count += 1;
                  }
                  return acc;
                },
                { sum: 0, count: 0 }
              );
              return {
                id: s.id,
                name: s.name,
                platform: s.platform,
                avgRating: count > 0 ? sum / count : 0,
                count: list.length,
              };
            }
            if (s.platform === "wildberries") {
              const url = getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS);
              const params = new URLSearchParams({
                take: String(100),
                skip: String(0),
                order: "dateDesc",
                storeId: s.id,
              });
              const resp = await fetch(`${url}?${params.toString()}`, {
                headers,
              });
              const data = await resp.json();
              const list: Array<{ productValuation?: number }> =
                data?.data?.feedbacks || [];
              const { sum, count } = list.reduce(
                (acc, r) => {
                  const rating = Number(r?.productValuation || 0);
                  if (Number.isFinite(rating) && rating > 0) {
                    acc.sum += rating;
                    acc.count += 1;
                  }
                  return acc;
                },
                { sum: 0, count: 0 }
              );
              return {
                id: s.id,
                name: s.name,
                platform: s.platform,
                avgRating: count > 0 ? sum / count : 0,
                count: list.length,
              };
            }
            if (s.platform === "yandex") {
              const url = getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS);
              const first = await fetch(`${url}?limit=50&storeId=${s.id}`, {
                headers,
              });
              const firstData = await first.json();
              const list1: Array<{ statistics?: { rating?: number } }> =
                firstData?.reviews || [];
              const secondToken =
                firstData?.result?.paging?.nextPageToken ||
                firstData?.nextPageToken;
              const second = secondToken
                ? await fetch(
                    `${url}?limit=50&storeId=${s.id}&pageToken=${encodeURIComponent(
                      secondToken
                    )}`,
                    { headers }
                  )
                : null;
              const secondData = second ? await second.json() : null;
              const list2: Array<{ statistics?: { rating?: number } }> =
                (secondData?.reviews as any[]) || [];
              const list = ([] as any[]).concat(list1, list2);
              const { sum, count } = list.reduce(
                (acc, r) => {
                  const rating = Number(r?.statistics?.rating || 0);
                  if (Number.isFinite(rating) && rating > 0) {
                    acc.sum += rating;
                    acc.count += 1;
                  }
                  return acc;
                },
                { sum: 0, count: 0 }
              );
              return {
                id: s.id,
                name: s.name,
                platform: s.platform,
                avgRating: count > 0 ? sum / count : 0,
                count: list.length,
              };
            }
            return {
              id: s.id,
              name: s.name,
              platform: s.platform,
              avgRating: 0,
              count: 0,
            };
          } catch (_) {
            return {
              id: s.id,
              name: s.name,
              platform: s.platform,
              avgRating: 0,
              count: 0,
            };
          }
        })
      );

      setStoresStats(perStoreStats);
    } catch (error) {
      // ignore stores stats errors
    } finally {
      setIsStoresLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Загрузка...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center text-gray-500 py-8">
        <p>Нет данных для отображения</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Статистика по магазинам — наверху, на всю ширину */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Статистика по магазинам
          </h3>
          <div className="text-xs text-gray-500">
            Рассчитано по последним 100 отзывам каждого магазина
          </div>
        </div>
        {isStoresLoading ? (
          <div className="flex justify-center items-center h-40">
            <div className="flex flex-col items-center gap-3">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600"
                aria-label="Загрузка"
              />
              <p className="text-sm text-gray-600">
                Загружаем статистику магазинов…
              </p>
            </div>
          </div>
        ) : storesStats.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center p-8 border border-dashed border-gray-200 rounded-2xl bg-gradient-to-br from-gray-50 to-white">
            <div className="mb-3 text-4xl">🛍️</div>
            <div className="text-base font-semibold text-gray-900 mb-1">
              Нет подключенных магазинов
            </div>
            <div className="text-sm text-gray-500 mb-4 max-w-md">
              Подключите хотя бы один магазин, чтобы мы могли собрать статистику
              по отзывам и показать сравнение.
            </div>
            <a
              href="/store-management"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors"
            >
              Перейти к подключению магазинов
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 7l5 5-5 5M6 12h12"
                ></path>
              </svg>
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {storesStats.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border border-gray-200 p-4 bg-gradient-to-br from-gray-50 to-white"
              >
                <div className="flex items-center justify-between mb-2">
                  <button
                    type="button"
                    className="text-sm font-semibold text-gray-800 truncate pr-2 hover:underline"
                    onClick={() => navigate(`/stores/${s.id}`)}
                  >
                    {s.name}
                  </button>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border ${getPlatformBadgeClasses(
                      s.platform
                    )}`}
                  >
                    {s.platform}
                  </span>
                </div>
                <div className="flex items-end gap-2">
                  <div className="text-2xl font-extrabold text-gray-900">
                    {s.avgRating.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-500">/ 5.00</div>
                </div>
                <div className="mt-2 flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`text-base ${i < Math.round(s.avgRating) ? "text-yellow-400" : "text-gray-200"}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <div className="mt-3 text-xs text-gray-500">
                  Учтено отзывов: {s.count}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Статистика по платформам — на всю ширину */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <ReviewsStatsChart
          platformDistribution={
            stats?.platformDistribution || {
              wildberries: 0,
              ozon: 0,
              yandex: 0,
            }
          }
          sentimentDistribution={
            stats?.sentimentDistribution || {
              positive: 0,
              negative: 0,
              neutral: 0,
            }
          }
          totalReviews={stats?.totalReviews || 0}
        />
      </div>

      {/* Нижние плашки удалены по запросу */}
    </div>
  );
};
